///
/// EarthBrowser
///
/// Created by Matt Giger on 12/3/2025
/// Copyright (c) 2025 Geodata Labs
///

import { isMobile } from "@/earthbrowser/utils/platform"
import { GestureState } from "@stovokor/gestures/GestureRecognizer"
import { PinchGestureRecognizer, type PinchGestureEventData } from "@stovokor/gestures/PinchGestureRecognizer"
import { ZoomGestureRecognizer, type ZoomGestureEventData } from "@stovokor/gestures/ZoomGestureRecognizer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import type { EntityCameraState } from "./EntityCameraState"

const cZoomSpringDamping = 15

/**
 * EntityZoomHandler - Zoom gesture recognizer for entity-relative camera
 *
 * Extends ZoomGestureRecognizer to handle both wheel and pinch-to-zoom gestures
 * for zooming the camera around a target entity (e.g., satellite).
 *
 * Zoom characteristics differ from Earth zoom:
 * - Uses radius-relative sensitivity (closer = finer control)
 * - Supports ctrl+wheel for 20x faster zoom
 */
export class EntityZoomHandler extends ZoomGestureRecognizer {
	private cameraState: EntityCameraState
	private context: StovokorContext

	// Pinch gesture state
	private initialScale = 1
	private gestureStartRadius = 0
	private gestureStartTheta = 0
	private gestureStartPhi = 0

	constructor(context: StovokorContext, cameraState: EntityCameraState) {
		super({
			minimumScaleChange: 0.02,
			rotationTolerance: 0.15,
			wheelSensitivity: 0.001,
			wheelEndDelay: 150
		})
		this.cameraState = cameraState
		this.context = context
		// Priority 700 ensures entity zoom always wins over Earth (which has priority 100 + max 500 distance boost)
		this.priority = 700

		this.onGesture(this.handleZoom.bind(this))
	}

	private handleZoom(data: ZoomGestureEventData): void {
		switch (data.state) {
			case GestureState.Began:
				this.handleZoomBegan(data)
				break
			case GestureState.Changed:
				this.handleZoomChanged(data)
				break
			case GestureState.Ended:
				this.handleZoomEnded(data)
				break
		}
	}

	private handleZoomBegan(data: ZoomGestureEventData): void {
		if (data.isWheelZoom) {
			// this.context.logger.log(
			// 	`[Entity] Wheel zoom began at (${data.x.toFixed(0)}, ${data.y.toFixed(0)})` +
			// 	(data.isTrackpadPinch ? " [trackpad pinch]" : "")
			// )
		} else {
			this.initialScale = data.scale
			this.captureStartState()
			// this.context.logger.log(
			// 	`[Entity] ✓ ENTITY PINCH ZOOM WON - Began at (${data.x.toFixed(0)}, ${data.y.toFixed(0)}) priority=${this.priority}`
			// )
		}
	}

	private handleZoomChanged(data: ZoomGestureEventData): void {
		if (data.isWheelZoom) {
			this.applyWheelZoom(data)
		} else {
			this.applyPinchZoom(data)
		}
	}

	private handleZoomEnded(_data: ZoomGestureEventData): void {
		// this.context.logger.log(
		// 	data.isWheelZoom ? `[Entity] Wheel zoom ended` : `[Entity] Pinch zoom ended`
		// )
	}

	/**
	 * Apply wheel zoom using entity-specific sensitivity
	 */
	private applyWheelZoom(data: ZoomGestureEventData): void {
		const current = this.cameraState.getCurrentParams()
		const length = current.radius

		// Entity wheel sensitivity: proportional to current radius
		const wheelSensitivity = length * 0.0091

		// Support ctrl+wheel for faster zoom (20x)
		const ctrlMultiplier = data.ctrlKey ? 20 : 1

		// Trackpad pinch gestures need different handling
		const trackpadMultiplier = data.isTrackpadPinch ? 200 : 1

		const delta = (data.wheelDeltaY ?? 0) * wheelSensitivity * ctrlMultiplier * trackpadMultiplier

		// Apply zoom - returns false if we've hit max range
		const handled = this.cameraState.applyZoomDelta(delta)

		// When zooming out and hitting max range, return to Earth view
		if (!handled && delta > 0) {
			this.context.logger.log("[Entity] Zoom out max reached - returning to Earth")
			// Preserve current camera state to avoid discontinuity
			this.context.focusManager.releaseFocus({ preserveCurrentState: true })
		}
	}

	/**
	 * Apply pinch zoom using entity-specific sensitivity
	 */
	private applyPinchZoom(data: ZoomGestureEventData): void {
		const scaleChange = data.scale / this.initialScale
		const sensitivity = isMobile() ? 1.0 : 0.8

		// Calculate new radius based on scale change
		const newRadius = this.gestureStartRadius / Math.pow(scaleChange, sensitivity)
		const clampedRadius = this.cameraState.elevationRange.clamp(newRadius)

		const newParams = this.cameraState.createParams(
			clampedRadius,
			this.gestureStartTheta,
			this.gestureStartPhi
		)

		this.cameraState.animateTo(newParams, cZoomSpringDamping)
	}

	private captureStartState(): void {
		const current = this.cameraState.getCurrentParams()
		this.gestureStartRadius = current.radius
		this.gestureStartTheta = current.theta
		this.gestureStartPhi = current.phi
	}

	override reset(): void {
		super.reset()
		this.initialScale = 1
		this.gestureStartRadius = 0
		this.gestureStartTheta = 0
		this.gestureStartPhi = 0
	}
}

/**
 * EntityPinchHandler - Generic pinch gesture handler for entity camera
 *
 * Extends PinchGestureRecognizer to handle pinch-to-zoom when
 * neither zoom nor rotate clearly dominates.
 *
 * Uses asymmetric sensitivity: faster zoom out, slower zoom in
 * for better control at close range.
 */
export class EntityPinchHandler extends PinchGestureRecognizer {
	private cameraState: EntityCameraState
	private context: StovokorContext

	// Pinch gesture state
	private anchorZoomSpacing = 0
	private gestureStartRadius = 0
	private gestureStartTheta = 0
	private gestureStartPhi = 0

	constructor(context: StovokorContext, cameraState: EntityCameraState) {
		super({
			minimumMovement: 0.01,
			allowSimultaneous: true
		})
		this.cameraState = cameraState
		this.context = context
		// Priority 650 ensures entity pinch always wins (slightly lower than zoom so zoom wins when both could apply)
		this.priority = 650

		this.onGesture(this.handlePinch.bind(this))
	}

	private handlePinch(data: PinchGestureEventData): void {
		switch (data.state) {
			case GestureState.Began:
				this.handlePinchBegan(data)
				break
			case GestureState.Changed:
				this.handlePinchChanged(data)
				break
			case GestureState.Ended:
				// Log if needed
				break
		}
	}

	private handlePinchBegan(data: PinchGestureEventData): void {
		this.anchorZoomSpacing = data.span
		this.captureStartState()
		this.context.logger.log(
			`[Entity] Generic pinch began at (${data.x.toFixed(0)}, ${data.y.toFixed(0)})`
		)
	}

	private handlePinchChanged(data: PinchGestureEventData): void {
		this.applyPinch(data)
	}

	/**
	 * Apply pinch with asymmetric sensitivity
	 * - Zooming in (negative delta): slower (0.0035)
	 * - Zooming out (positive delta): faster (0.0065)
	 */
	private applyPinch(data: PinchGestureEventData): void {
		const deltaDistance = this.anchorZoomSpacing - data.span
		const length = this.gestureStartRadius

		// Asymmetric multiplier: slower zoom in, faster zoom out
		let multiplier: number
		if (deltaDistance > 0) {
			// Zooming in - slow down for precision
			multiplier = 0.0035
		} else {
			// Zooming out - speed up
			multiplier = 0.0065
		}

		const sensitivity = length * multiplier
		const delta = deltaDistance * sensitivity

		const newRadius = this.cameraState.elevationRange.clamp(this.gestureStartRadius + delta)

		const newParams = this.cameraState.createParams(
			newRadius,
			this.gestureStartTheta,
			this.gestureStartPhi
		)

		this.cameraState.animateTo(newParams, cZoomSpringDamping)
	}

	private captureStartState(): void {
		const current = this.cameraState.getCurrentParams()
		this.gestureStartRadius = current.radius
		this.gestureStartTheta = current.theta
		this.gestureStartPhi = current.phi
	}

	override reset(): void {
		super.reset()
		this.anchorZoomSpacing = 0
		this.gestureStartRadius = 0
		this.gestureStartTheta = 0
		this.gestureStartPhi = 0
	}
}

